import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="time-tracker"
//
// Counts seconds of active engagement and periodically flushes
// the accumulated total to the server. Pauses automatically when
// the browser tab is hidden.
//
// Required values:
//   data-time-tracker-url-value       - POST endpoint (api_time_spent_path)
//   data-time-tracker-model-value     - registry key, e.g. "scripture_dig_progress"
//   data-time-tracker-record-id-value - UUID of the record to update; may be set later
//
// Optional values:
//   data-time-tracker-flush-interval-value - seconds between flushes (default: 30)
export default class extends Controller {
  static targets = ['timeInput']

  static values = {
    url: String,
    model: String,
    recordId: String,
    flushInterval: { type: Number, default: 30 }
  }

  connect () {
    this._pending = 0
    this._ticker = null
    this._flusher = null

    this._boundOnVisibilityChange = this._onVisibilityChange.bind(this)
    this._boundOnPageHide = this._onPageHide.bind(this)
    this._boundOnRecordId = this._onRecordId.bind(this)
    this._boundOnSubmit = this._onSubmit.bind(this)

    document.addEventListener('visibilitychange', this._boundOnVisibilityChange)
    window.addEventListener('pagehide', this._boundOnPageHide)
    this.element.addEventListener('time-tracker:record-id', this._boundOnRecordId)
    this.element.addEventListener('submit', this._boundOnSubmit)

    if (!document.hidden) this._start()
  }

  disconnect () {
    document.removeEventListener('visibilitychange', this._boundOnVisibilityChange)
    window.removeEventListener('pagehide', this._boundOnPageHide)
    this.element.removeEventListener('time-tracker:record-id', this._boundOnRecordId)
    this.element.removeEventListener('submit', this._boundOnSubmit)
    this._stop()
    this._flush()
  }

  // -- Private --

  _start () {
    if (this._ticker) return

    this._ticker = setInterval(() => {
      this._pending += 1
      this._syncTimeInput()
    }, 1000)
    this._flusher = setInterval(() => { this._flush() }, this.flushIntervalValue * 1000)
  }

  _stop () {
    clearInterval(this._ticker)
    clearInterval(this._flusher)
    this._ticker = null
    this._flusher = null
  }

  _flush () {
    if (!this.hasRecordIdValue || this.recordIdValue === '') return
    if (this._pending <= 0) return

    const seconds = this._pending
    this._pending = 0
    this._syncTimeInput()

    const body = new FormData()
    body.append('model', this.modelValue)
    body.append('id', this.recordIdValue)
    body.append('seconds', seconds)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    fetch(this.urlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      body,
      keepalive: true
    }).catch(() => {
      // On failure, return the seconds to the pending bucket so they
      // are not silently lost — they will be retried on the next flush.
      this._pending += seconds
    })
  }

  _onPageHide () {
    this._stop()
    this._flush()
  }

  _onRecordId (event) {
    this.recordIdValue = event.detail.recordId
    if (!document.hidden) this._start()
    this._flush()
  }

  _onSubmit () {
    this._syncTimeInput()
    this._pending = 0
  }

  _syncTimeInput () {
    if (!this.hasTimeInputTarget) return

    this.timeInputTarget.value = this._pending
  }

  _onVisibilityChange () {
    if (document.hidden) {
      this._stop()
      this._flush()
    } else {
      this._start()
    }
  }
}
